import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { api } from '../../api';
import { useLocale } from '../../context';
import './home-match-carousel.css';

const POLL_MS = 3000;
const FLASH_MS = 1800;
const REFILL_MS = 500;

const SPORT_PILL = {
  1: '#619A38',
  2: '#C45C26',
  3: '#2F6FED',
  4: '#8B5CF6',
  5: '#0EA5E9',
  12: '#E11D48',
  13: '#D97706',
  66: '#059669',
};

const SPORT_BG_BY_ID = {
  1: '/top-events/football.jpg',
  2: '/top-events/basketball.jpg',
  3: '/top-events/tennis.jpg',
  4: '/top-events/hockey.jpg',
  5: '/top-events/volleyball.jpg',
  6: '/top-events/handball.jpg',
  11: '/top-events/baseball.jpg',
  12: '/top-events/football.jpg',
  13: '/top-events/football.jpg',
  29: '/top-events/tennis.jpg',
  66: '/top-events/cricket.jpg',
  75: '/top-events/badminton.jpg',
};

function sportPillColor(sportId) {
  return SPORT_PILL[Number(sportId)] || '#5B6B7C';
}

function sportBackground(ev) {
  const byId = SPORT_BG_BY_ID[Number(ev?.sport_id)];
  if (byId) return byId;
  const name = String(ev?.sport_name || '').toLowerCase();
  if (/futbol|football|soccer|amerikan/.test(name)) return '/top-events/football.jpg';
  if (/basket/.test(name)) return '/top-events/basketball.jpg';
  if (/tenis|tennis|masa\s*tenisi/.test(name)) return '/top-events/tennis.jpg';
  if (/hokey|hockey/.test(name)) return '/top-events/hockey.jpg';
  if (/voleybol|volleyball/.test(name)) return '/top-events/volleyball.jpg';
  if (/hentbol|handball/.test(name)) return '/top-events/handball.jpg';
  if (/baseball/.test(name)) return '/top-events/baseball.jpg';
  if (/kriket|cricket/.test(name)) return '/top-events/cricket.jpg';
  if (/badminton/.test(name)) return '/top-events/badminton.jpg';
  return '/top-events/football.jpg';
}

function IconMatches() {
  return (
    <svg
      className="home-match-carousel__icon"
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <path
        fill="currentColor"
        d="M21.722 2.2c0-1.03-.836-1.867-1.867-1.867H4.145c-1.032 0-1.868.836-1.868 1.867l-1.944.078v1.944c0 4.753 3.759 8.643 8.459 8.857q.464.263.957.45l-1.638 8.193h-.972a.972.972 0 1 0 0 1.945h9.722a.972.972 0 1 0 0-1.945h-.972L14.25 13.53q.494-.187.957-.451c4.7-.214 8.46-4.104 8.46-8.857V2.278zM2.51 4.222h1.633s.624 4.086 1.548 5.685A6.68 6.68 0 0 1 2.51 4.222m15.797 5.685c.924-1.6 1.547-5.685 1.547-5.685h1.633a6.68 6.68 0 0 1-3.18 5.685"
      />
    </svg>
  );
}

function TeamLogo({ src, name }) {
  const [failed, setFailed] = useState(false);
  const initials = String(name || '')
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase() || '?';

  if (src && !failed) {
    return (
      <img
        className="home-match-card__logo home-match-card__logo--img"
        src={src}
        alt=""
        width={26}
        height={26}
        loading="lazy"
        onError={() => setFailed(true)}
      />
    );
  }

  return (
    <span className="home-match-card__logo" aria-hidden="true">
      {initials}
    </span>
  );
}

function formatKickoffParts(startTime, locale) {
  if (!startTime) return { time: '', date: '' };
  const date = new Date(startTime);
  if (Number.isNaN(date.getTime())) return { time: '', date: '' };
  const now = new Date();
  const sameDay = date.toDateString() === now.toDateString();
  const time = date.toLocaleTimeString(locale === 'tr' ? 'tr-TR' : 'en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  });
  if (sameDay) {
    return { time, date: locale === 'en' ? 'Today' : 'Bugün' };
  }
  const day = date.toLocaleDateString(locale === 'tr' ? 'tr-TR' : 'en-GB', {
    day: 'numeric',
    month: 'short',
  });
  return { time, date: day };
}

function periodLabel(ev) {
  if (!ev.is_live) return '';
  const raw = String(ev.live_minute || '').trim();
  if (!raw || /^live$/i.test(raw) || /^canlı$/i.test(raw)) return '';
  return raw;
}

function formatTimerSeconds(sec) {
  const s = Math.max(0, Math.floor(Number(sec) || 0));
  const mm = Math.floor(s / 60);
  const ss = s % 60;
  return `${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`;
}

function isTimerPaused(period) {
  return /devre\s*aras|half[\s-]?time|\bht\b|mola|time[\s-]?out|\bara\b|break|pause/i.test(
    String(period || ''),
  );
}

function liveTimerBase(ev) {
  const timer = Number(ev.live_timer);
  if (Number.isFinite(timer) && timer >= 0) return timer;
  const clock = String(ev.live_clock || '').trim();
  const m = clock.match(/^(\d{1,3}):(\d{2})$/);
  if (!m) return null;
  return Number(m[1]) * 60 + Number(m[2]);
}

function TimerIcon() {
  return (
    <svg
      className="home-match-card__timer-svg"
      xmlns="http://www.w3.org/2000/svg"
      width="14"
      height="14"
      viewBox="0 0 14 14"
      fill="none"
      aria-hidden="true"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M6.99988 1.16699C3.77801 1.16699 1.16655 3.77845 1.16655 7.00033C1.16655 10.2222 3.77801 12.8337 6.99988 12.8337C10.2218 12.8337 12.8332 10.2222 12.8332 7.00033C12.8332 3.77845 10.2218 1.16699 6.99988 1.16699ZM6.99988 2.04199C9.73851 2.04199 11.9582 4.2617 11.9582 7.00033C11.9582 9.73895 9.73851 11.9587 6.99988 11.9587C4.26126 11.9587 2.04155 9.73895 2.04155 7.00033C2.04155 4.2617 4.26126 2.04199 6.99988 2.04199ZM7.23599 4.51803C7.20702 4.30448 7.02397 4.13989 6.80248 4.13989C6.56086 4.13989 6.36498 4.33577 6.36498 4.57739V7.40481L6.36986 7.46999C6.38919 7.59836 6.46493 7.71287 6.57835 7.78053L8.77751 9.09245L8.83054 9.11943C9.02877 9.20396 9.2639 9.13108 9.37737 8.94086L9.40436 8.88784C9.48888 8.6896 9.416 8.45447 9.22579 8.341L7.23975 7.15578L7.23998 4.57739L7.23599 4.51803Z"
        fill="currentColor"
      />
    </svg>
  );
}

function LiveTimeInfo({ baseSeconds, period, syncedAt, locale }) {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);

  const base = Number(baseSeconds);
  const hasBase = Number.isFinite(base) && base >= 0;
  const paused = isTimerPaused(period);
  const elapsed =
    hasBase && !paused && Number.isFinite(syncedAt)
      ? Math.max(0, Math.floor((now - syncedAt) / 1000))
      : 0;
  const clock = hasBase ? formatTimerSeconds(base + elapsed) : '';

  if (!clock && !period) {
    return (
      <div className="home-match-card__time-info">
        <span className="home-match-card__time-wrap">
          <span className="home-match-card__timer-icon">
            <TimerIcon />
          </span>
          <span className="home-match-card__live-period is-pulse">
            {locale === 'en' ? 'Live' : 'Canlı'}
          </span>
        </span>
      </div>
    );
  }

  return (
    <div className="home-match-card__time-info">
      <span className="home-match-card__time-wrap">
        {clock ? (
          <>
            <span className="home-match-card__timer-icon">
              <TimerIcon />
            </span>
            <span className="home-match-card__live-clock">{clock}</span>
          </>
        ) : null}
        {period ? <span className="home-match-card__live-period">{period}</span> : null}
      </span>
    </div>
  );
}

function formatOdd(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return '—';
  return n % 1 === 0 ? String(n) : n.toFixed(2);
}

function oddKey(matchId, label) {
  return `${matchId}::${label}`;
}

function scoreKey(matchId) {
  return `score::${matchId}`;
}

function OddsRow({ odds, onOddClick }) {
  const list = Array.isArray(odds) ? odds : [];
  if (!list.length) return null;
  return (
    <div className="home-match-card__odds">
      {list.map((odd) => {
        const tick = odd.tick === 'up' || odd.tick === 'down' ? odd.tick : null;
        const shown = odd.blocked || odd.value == null ? '—' : formatOdd(odd.value);
        const blocked = Boolean(odd.blocked) || odd.value == null;
        return (
          <button
            key={`${odd.label}-${shown}-${tick || 'x'}`}
            type="button"
            className={`home-match-card__odd${blocked ? ' is-blocked' : ''}${tick ? ` is-${tick}` : ''}`}
            data-match-odd-box="true"
            disabled={blocked}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (!blocked) onOddClick?.(odd);
            }}
          >
            <span className="home-match-card__odd-label">{odd.label}</span>
            <span className={`home-match-card__odd-value${tick ? ` is-${tick}` : ''}`}>
              {shown}
            </span>
          </button>
        );
      })}
    </div>
  );
}

function sportEventFromMatch(ev, odd = null) {
  const event = {
    type: ev?.type === 'live' || ev?.is_live ? 'live' : 'line',
    sportId: Number(ev?.sport_id) || 0,
    leagueId: Number(ev?.league_id) || 0,
    gameId: Number(ev?.id) || 0,
  };
  if (odd && !odd.blocked) {
    event.marketId = Number(odd.market_id) || 0;
    event.oddId = Number(odd.id) || 0;
    event.oddPoint = odd.point ? String(odd.point) : '';
    event.opId = odd.op_id ?? null;
    event.oddLabel = odd.label || '';
    event.oddRate = odd.value;
  }
  return event;
}

function MatchCard({ ev, locale, onOpen, onOddOpen }) {
  const live = Boolean(ev.is_live);
  const period = periodLabel(ev);
  const timerBase = live
    ? (Number.isFinite(ev._timerBase) ? ev._timerBase : liveTimerBase(ev))
    : null;
  const kickoff = live ? null : formatKickoffParts(ev.start_time, locale);
  const pillColor = sportPillColor(ev.sport_id);
  const bg = sportBackground(ev);
  const homeFlash = ev.scoreFlashHome;
  const awayFlash = ev.scoreFlashAway;

  return (
    <div
      role="button"
      tabIndex={0}
      className={`home-match-card${live ? ' is-live' : ''}`}
      style={{ '--match-sport': pillColor }}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen?.();
        }
      }}
    >
      <img
        className="home-match-card__bg-img"
        src={bg}
        alt=""
        loading="lazy"
        decoding="async"
        aria-hidden="true"
      />
      <span className="home-match-card__shade" aria-hidden="true" />

      <div className="home-match-card__header">
        <span className="home-match-card__sport-pill">
          <span className="home-match-card__sport-pill-bg" aria-hidden="true" />
          <span className="home-match-card__sport-pill-text">{ev.sport_name || 'Sport'}</span>
        </span>
        <span className="home-match-card__league">{ev.league || ev.sport_name}</span>
        {live ? (
          <span className="home-match-card__live-badge" aria-label={locale === 'en' ? 'Live' : 'Canlı'}>
            {locale === 'en' ? 'Live' : 'Canlı'}
          </span>
        ) : null}
      </div>

      {live ? (
        <div className="home-match-card__live-info">
          <div className="home-match-card__teams-section">
            <LiveTimeInfo
              baseSeconds={timerBase}
              period={period}
              syncedAt={ev._timerSyncedAt}
              locale={locale}
            />
            <div className="home-match-card__team-row">
              <TeamLogo src={ev.home_icon} name={ev.home_team} />
              <span className="home-match-card__team-name">{ev.home_team}</span>
            </div>
            <div className="home-match-card__team-row">
              <TeamLogo src={ev.away_icon} name={ev.away_team} />
              <span className="home-match-card__team-name">{ev.away_team}</span>
            </div>
          </div>
          {ev.home_score != null && ev.away_score != null ? (
            <div
              className={`home-match-card__score-board${ev.scoreFlash ? ' is-changed' : ''}`}
              aria-label={`${ev.home_score} - ${ev.away_score}`}
            >
              <div className="home-match-card__score-col">
                <span
                  key={`h-${ev.home_score}-${homeFlash ? 'f' : 'n'}`}
                  className={`home-match-card__score-num${homeFlash ? ' is-changed' : ''}`}
                >
                  {ev.home_score}
                </span>
                <span
                  key={`a-${ev.away_score}-${awayFlash ? 'f' : 'n'}`}
                  className={`home-match-card__score-num${awayFlash ? ' is-changed' : ''}`}
                >
                  {ev.away_score}
                </span>
              </div>
            </div>
          ) : null}
        </div>
      ) : (
        <div className="home-match-card__body">
          <div className="home-match-card__team">
            <TeamLogo src={ev.home_icon} name={ev.home_team} />
            <span className="home-match-card__team-name">{ev.home_team}</span>
          </div>
          <div className="home-match-card__center">
            <span className="home-match-card__kickoff-time">{kickoff?.time}</span>
            <span className="home-match-card__kickoff-date">{kickoff?.date}</span>
          </div>
          <div className="home-match-card__team">
            <TeamLogo src={ev.away_icon} name={ev.away_team} />
            <span className="home-match-card__team-name">{ev.away_team}</span>
          </div>
        </div>
      )}

      <OddsRow odds={ev.odds} onOddClick={(odd) => onOddOpen?.(odd)} />
    </div>
  );
}

function withMatchTicks(nextList, prevOddsMap, prevScoreMap, prevEventsMap) {
  const nextOddsMap = new Map();
  const nextScoreMap = new Map();
  const stamped = (Array.isArray(nextList) ? nextList : []).map((ev) => {
    const odds = (Array.isArray(ev.odds) ? ev.odds : []).map((odd) => {
      const key = oddKey(ev.id, odd.label);
      const nextVal = Number(odd.value);
      const prevVal = prevOddsMap.get(key);
      nextOddsMap.set(key, nextVal);
      let tick = null;
      if (
        Number.isFinite(prevVal)
        && Number.isFinite(nextVal)
        && nextVal > 0
        && Math.abs(nextVal - prevVal) >= 0.001
      ) {
        tick = nextVal > prevVal ? 'up' : 'down';
      }
      return { ...odd, tick };
    });

    const sk = scoreKey(ev.id);
    const nextScore = `${ev.home_score ?? ''}:${ev.away_score ?? ''}`;
    const prevScore = prevScoreMap.get(sk);
    nextScoreMap.set(sk, nextScore);
    let scoreFlash = false;
    let scoreFlashHome = false;
    let scoreFlashAway = false;
    if (prevScore != null && prevScore !== nextScore) {
      scoreFlash = true;
      const [ph, pa] = String(prevScore).split(':');
      scoreFlashHome = String(ev.home_score ?? '') !== ph;
      scoreFlashAway = String(ev.away_score ?? '') !== pa;
    }

    const prevEv = prevEventsMap?.get(ev.id);
    const nextTimer = liveTimerBase(ev);
    let timerBase = nextTimer;
    let timerSyncedAt = Date.now();
    if (prevEv && Number.isFinite(prevEv._timerSyncedAt) && Number.isFinite(prevEv._timerBase)) {
      const localNow = prevEv._timerBase
        + Math.max(0, Math.floor((Date.now() - prevEv._timerSyncedAt) / 1000));
      if (nextTimer == null) {
        timerBase = prevEv._timerBase;
        timerSyncedAt = prevEv._timerSyncedAt;
      } else if (Math.abs(nextTimer - localNow) <= 2) {
        timerBase = prevEv._timerBase;
        timerSyncedAt = prevEv._timerSyncedAt;
      } else if (nextTimer > localNow + 2) {
        timerBase = nextTimer;
        timerSyncedAt = Date.now();
      } else if (nextTimer < localNow - 20) {
        timerBase = nextTimer;
        timerSyncedAt = Date.now();
      } else {
        timerBase = prevEv._timerBase;
        timerSyncedAt = prevEv._timerSyncedAt;
      }
    } else if (nextTimer == null) {
      timerBase = null;
    }

    return {
      ...ev,
      odds,
      scoreFlash,
      scoreFlashHome,
      scoreFlashAway,
      _timerBase: timerBase,
      _timerSyncedAt: timerSyncedAt,
    };
  });
  return { stamped, nextOddsMap, nextScoreMap };
}

export default function HomeMatchCarousel({ onNavigate }) {
  const _locale = useLocale();
  const tr = _locale.t;
  const locale = _locale.locale;
  const titleId = useId();
  const viewportRef = useRef(null);
  const prevOddsRef = useRef(new Map());
  const prevScoresRef = useRef(new Map());
  const prevEventsRef = useRef(new Map());
  const prevIdsRef = useRef([]);
  const flashTimerRef = useRef(null);
  const refillTimerRef = useRef(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);

  const applyMatches = useCallback((list, { flash = true } = {}) => {
    const now = Date.now();
    const active = (Array.isArray(list) ? list : []).filter((ev) => {
      if (!ev || ev.finished) return false;
      if (ev.is_live || ev.type === 'live') return true;
      if (!ev.start_time) return false;
      const t = new Date(ev.start_time).getTime();
      return Number.isFinite(t) && t > now;
    });
    const { stamped, nextOddsMap, nextScoreMap } = withMatchTicks(
      active,
      prevOddsRef.current,
      prevScoresRef.current,
      prevEventsRef.current,
    );
    const nextIds = stamped.map((ev) => ev.id);
    const removedLive = prevIdsRef.current.filter((id) => !nextIds.includes(id));
    prevOddsRef.current = nextOddsMap;
    prevScoresRef.current = nextScoreMap;
    prevEventsRef.current = new Map(stamped.map((ev) => [ev.id, ev]));
    prevIdsRef.current = nextIds;
    setEvents(stamped);

    if (flashTimerRef.current) {
      window.clearTimeout(flashTimerRef.current);
      flashTimerRef.current = null;
    }
    const hasFlash = flash && stamped.some((ev) => (
      ev.scoreFlash
      || (ev.odds || []).some((o) => o.tick)
    ));
    if (hasFlash) {
      flashTimerRef.current = window.setTimeout(() => {
        setEvents((curr) => curr.map((ev) => ({
          ...ev,
          scoreFlash: false,
          scoreFlashHome: false,
          scoreFlashAway: false,
          odds: (ev.odds || []).map((odd) => (odd.tick ? { ...odd, tick: null } : odd)),
        })));
        flashTimerRef.current = null;
      }, FLASH_MS);
    }

    return { removedLiveCount: removedLive.length };
  }, []);

  const loadMatches = useCallback(async ({ silent = false, forceRefill = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const data = await api.nexusTopMatches({ lang: locale === 'en' ? 'en' : 'tr' });
      const list = Array.isArray(data?.matches) ? data.matches : [];
      const playable = list.filter((ev) => (
        ev
        && !ev.gr8_demo
        && Number(ev.league_id) > 0
        && Number(ev.id) > 0
        && Number(ev.sport_id) > 0
        && ev.home_team
        && ev.away_team
      ));
      if (playable.length || !silent) {
        const result = applyMatches(playable, {
          flash: silent || prevOddsRef.current.size > 0 || prevScoresRef.current.size > 0,
        });
        if (!forceRefill && result?.removedLiveCount > 0) {
          if (refillTimerRef.current) window.clearTimeout(refillTimerRef.current);
          refillTimerRef.current = window.setTimeout(() => {
            refillTimerRef.current = null;
            loadMatches({ silent: true, forceRefill: true });
          }, REFILL_MS);
        }
      }
    } catch {
      if (!silent) setEvents([]);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [locale, applyMatches]);

  useEffect(() => {
    let alive = true;
    let timer = null;

    const tick = async (silent) => {
      if (!alive) return;
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      await loadMatches({ silent });
    };

    tick(false);
    timer = window.setInterval(() => {
      tick(true);
    }, POLL_MS);

    const onVisibility = () => {
      if (document.visibilityState === 'visible') tick(true);
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      alive = false;
      if (timer) window.clearInterval(timer);
      if (flashTimerRef.current) window.clearTimeout(flashTimerRef.current);
      if (refillTimerRef.current) window.clearTimeout(refillTimerRef.current);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [loadMatches]);

  const items = useMemo(
    () => events.filter((ev) => ev?.home_team && ev?.away_team).slice(0, 24),
    [events],
  );

  const syncNav = useCallback(() => {
    const el = viewportRef.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    setCanPrev(el.scrollLeft > 4);
    setCanNext(el.scrollLeft < max - 4);
  }, []);

  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return undefined;
    syncNav();
    el.addEventListener('scroll', syncNav, { passive: true });
    window.addEventListener('resize', syncNav);
    let ro;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(syncNav);
      ro.observe(el);
    }
    return () => {
      el.removeEventListener('scroll', syncNav);
      window.removeEventListener('resize', syncNav);
      ro?.disconnect();
    };
  }, [syncNav, items.length]);

  const scrollByPage = (dir) => {
    const el = viewportRef.current;
    if (!el) return;
    const amount = Math.max(280, Math.floor(el.clientWidth * 0.85)) * dir;
    el.scrollBy({ left: amount, behavior: 'smooth' });
  };

  const openSport = () => onNavigate?.('sport');
  const openLiveTv = () => onNavigate?.('vurgunTv');
  const openMatch = (ev) => {
    const sportEvent = sportEventFromMatch(ev);
    if (sportEvent.gameId && sportEvent.sportId && sportEvent.leagueId) {
      onNavigate?.('sport', { sportEvent, sportFilter: sportEvent.type === 'live' ? 'live' : undefined });
      return;
    }
    openSport();
  };
  const openOdd = (ev, odd) => {
    const sportEvent = sportEventFromMatch(ev, odd);
    if (sportEvent.gameId && sportEvent.sportId && sportEvent.leagueId) {
      onNavigate?.('sport', { sportEvent, sportFilter: sportEvent.type === 'live' ? 'live' : undefined });
      return;
    }
    openSport();
  };

  if (!loading && items.length === 0) return null;

  return (
    <section className="home-match-carousel" aria-labelledby={titleId}>
      <div className="home-match-carousel__header">
        <div className="home-match-carousel__header-left">
          <IconMatches />
          <h2 className="home-match-carousel__title" id={titleId}>
            {tr('home.matchCarousel.title') || (locale === 'tr' ? 'Maçlar' : 'Matches')}
          </h2>
        </div>
        <div className="home-match-carousel__actions">
          <button
            type="button"
            className="home-match-carousel__live-tv"
            onClick={openLiveTv}
          >
            <span className="home-match-carousel__live-tv-dot" aria-hidden="true" />
            {(tr('home.matchCarousel.liveTv') === 'home.matchCarousel.liveTv'
              ? (locale === 'en' ? 'Live TV' : 'Canlı TV')
              : tr('home.matchCarousel.liveTv'))}
          </button>
          <button
            type="button"
            className="home-match-carousel__show-all"
            onClick={openSport}
          >
            {tr('home.slotsCarousel.showAll') || 'Tümünü göster'}
          </button>
          <button
            type="button"
            className="home-match-carousel__nav"
            data-placement="left"
            aria-label={tr('home.slotsCarousel.prev') || 'Önceki'}
            disabled={!canPrev}
            onClick={() => scrollByPage(-1)}
          />
          <button
            type="button"
            className="home-match-carousel__nav"
            data-placement="right"
            aria-label={tr('home.slotsCarousel.next') || 'Sonraki'}
            disabled={!canNext}
            onClick={() => scrollByPage(1)}
          />
        </div>
      </div>

      <div className="home-match-carousel__viewport" ref={viewportRef}>
        <div className="home-match-carousel__track">
          {loading && items.length === 0
            ? Array.from({ length: 4 }, (_, i) => (
              <div key={`sk-${i}`} className="home-match-card home-match-card--skeleton" aria-hidden="true" />
            ))
            : items.map((ev) => (
              <MatchCard
                key={ev.id}
                ev={ev}
                locale={locale}
                onOpen={() => openMatch(ev)}
                onOddOpen={(odd) => openOdd(ev, odd)}
              />
            ))}
        </div>
      </div>
    </section>
  );
}
